<script setup lang="ts" generic="R extends object">
import { useI18n } from "vue-i18n";
import { computed } from "vue";

import type { iconName } from "@/Modules/App/Models/Styles/Icons";

import { TcLoader } from "@/Components/UI";
import TcIcon from "@/Components/Extend/TcIcon.vue";

export interface TableColumn<R> {
  key: keyof R;
  label: string;
}

export interface TcTableProps<T> {
  columns: TableColumn<T>[];
  data: T[];
  extraFields?: (keyof T)[];
  actionsColumnTitle: string;
  isLoading?: boolean;
  iconEmptySearch: iconName;
}

const { t } = useI18n();

const props = defineProps<TcTableProps<R>>();

const visibleFields = computed(() => props.columns.map((col: TableColumn<R>) => col.key));
const filteredData = computed((): R[] =>
  filterObjectFields(props.data, visibleFields.value, props.extraFields)
);

function filterObjectFields(data: R[], visibleFields: (keyof R)[], extraFields: (keyof R)[] = []): R[] {
  const set = new Set<keyof R>();

  for (const field of visibleFields) {
    set.add(field);
  }

  for (const field of extraFields) {
    set.add(field);
  }

  const allFields = Array.from(set);

  return data.map((item) => {
    const filtered = {} as R;

    for (const field of allFields) {
      if (field in (item as R)) {
        filtered[field] = item[field];
      }
    }

    return filtered;
  });
}
</script>

<template>
  <table class="tc-table">
    <thead>
      <tr>
        <th v-for="col in props.columns" :key="col.key">{{ col.label }}</th>
        <th class="tc-table__actions">
          <slot name="actions-header">
            {{ props.actionsColumnTitle }}
          </slot>
        </th>
      </tr>
    </thead>

    <tbody>
      <tr v-if="props.isLoading">
        <td :colspan="props.columns.length + 1" class="tc-table__loader">
          <TcLoader :center="true" />
        </td>
      </tr>

      <tr v-else-if="!props.data.length">
        <td :colspan="props.columns.length + 1" class="tc-table__empty">
          <slot name="empty">
            <div class="tc-table__empty__message">
              <TcIcon :name="iconEmptySearch" size="xxl" />
              <span>{{ t("empty_data") }}</span>
            </div>
          </slot>
        </td>
      </tr>

      <tr v-else v-for="(row, index) in filteredData" :key="index">
        <td v-for="col in columns" :key="col.key">
          <slot :name="`body-${String(col.key)}`" :row="row" :key="col.key" :value="row[col.key]" />
        </td>

        <td>
          <slot name="actions" :row="row" />
        </td>
      </tr>
    </tbody>
  </table>
</template>

<style scoped lang="scss">
.tc-table {
  @include utils.noScrollbar();

  width: 100%;
  height: 100%;
  border-collapse: collapse;

  th,
  td {
    @include sp.padding(x, 4);
    @include sp.padding(y, 3);
    @include text.modification(left);
    @include border(1, solid, b);
    @include colors.border(content, low);
  }

  th {
    @include text.typo(low, m);
    @include colors.color(content, mid);
    @include colors.bg(content, low);
  }

  th.tc-table__actions {
    @include text.modification(center);
  }

  &__empty {
    height: calc(100vh - ($space-24 * 2));
    padding: 0 !important;

    &__message {
      height: 100%;
      width: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
      flex-direction: column;

      @include text.display(low, xl);
    }
  }
}
</style>
